(window as any).global = window;
(window as any).process = {
  env: { DEBUG: undefined },
};

import { bootstrapApplication } from "@angular/platform-browser";
import * as Sentry from "@sentry/angular";

import { AppComponent } from "./app/app.component";
import { appConfig } from "./app/app.config";
import { environment } from "./environments/environment";

// Skip Sentry transmission on localhost (any port, any build flavor) so dev
// noise / E2E runs don't pollute prod issues. The SDK still initialises so
// in-memory hub APIs (setUser / setTag / addBreadcrumb) keep working — only
// network transmission is suppressed via `enabled`.
const _sentryHostname =
  typeof window !== "undefined" ? window.location.hostname : "";
const _sentryIsLocalhost =
  _sentryHostname === "" ||
  /^(localhost|127\.0\.0\.1|0\.0\.0\.0|::1)$/.test(_sentryHostname) ||
  _sentryHostname.endsWith(".local");

Sentry.init({
  dsn: environment.sentry.dsn,
  environment: environment.sentry.environment,
  dataCollection: {
    cookies: false,
    httpHeaders: false,
    httpBodies: [],
    databaseQueryData: false,
    genAI: { inputs: false, outputs: false },
    queues: false,
    stackFrameVariables: false,
  },
  // Don't transmit from localhost. Keeps dev / E2E noise out of prod issues.
  enabled: !_sentryIsLocalhost,
  // Only inject `sentry-trace` / `baggage` headers on calls to OUR API.
  // Without this, BrowserTracing would propagate them to every outgoing
  // request — Firebase Storage's CORS policy doesn't whitelist those
  // headers, which would block PDF / file downloads via preflight (same
  tracePropagationTargets: [environment.apiUrl],
  // Drop expected/benign errors before transmission. These are NOT bugs:
  //   - Auth0 silent refresh fails when the session is gone (SDK redirects
  //     to login automatically — user never sees broken state).
  //   - Chunk-load errors fire when a patient has a tab open across a
  //     deploy (refresh fixes them).
  //   - AbortError fires when the user navigates while an XHR is in flight.
  //   - ResizeObserver loop warnings are a benign Chrome/Safari quirk.
  //   - "Non-Error promise rejection captured" almost always comes from
  //     third-party deps that throw strings instead of Errors.
  ignoreErrors: [
    "Login required",
    "Consent required",
    "Failed to fetch dynamically imported module",
    "Importing a module script failed",
    "ChunkLoadError",
    "Loading chunk",
    "Loading CSS chunk",
    "AbortError",
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications",
    "Non-Error promise rejection captured",
  ],
  // Skip events whose stack trace originates inside browser extensions.
  // Grammarly / password managers / ad blockers inject scripts into our
  // pages and throw their own errors — none of which are our bugs.
  denyUrls: [
    /chrome-extension:\/\//i,
    /moz-extension:\/\//i,
    /safari-extension:\/\//i,
    /safari-web-extension:\/\//i,
  ],
});

// Verification helpers — call from DevTools console to confirm Sentry capture.
(window as any).Sentry = Sentry;
(window as any).captureTestSentry = async () => {
  const id = Sentry.captureMessage(
    "Manual test from uplift-user console",
    "error",
  );
  const flushed = await Sentry.flush(5000);
  console.log("[sentry] event id:", id, "flushed:", flushed);
  return { id, flushed };
};
(window as any).throwTestSentryError = () => {
  throw new Error("Sentry Test Error — uplift-user");
};
import {
  defineCustomElements,
  applyPolyfills
} from "mindlife-custom-battery-rangeslider/loader";

bootstrapApplication(AppComponent, appConfig).catch((err) => console.error(err));
applyPolyfills().then(() => defineCustomElements());
